/**
 * 全国省市中英文映射数据（TypeScript 版）
 * 包含：34 个省级行政区 + 主要城市
 *
 * 用法：
 *   import { provinceMap, cityMap, cnToEn, enToCn } from './china-geo-map';
 *
 * 类型声明文件（.d.ts）可由此文件自动生成：
 *   tsc --declaration --emitDeclarationOnly china-geo-map.ts
 */

// ==================== 类型定义 ====================

/** 转换类型：自动检测 / 仅省级 / 仅城市 */
export type TranslateType = 'auto' | 'province' | 'city';

/** 批量转换方向 */
export type BatchDirection = 'cnToEn' | 'enToCn' | 'auto';

/** 批量转换选项 */
export interface BatchTranslateOptions {
  direction?: BatchDirection;
  type?: TranslateType;
}

/** 已知冲突项的内部结构 */
interface ConflictEntry {
  default: string;
  alternatives: string[];
}

/** 反向映射表类型（键为英文，值为中文） */
type ReverseMap = Record<string, string>;

// ==================== 省级行政区映射 ====================

/** 省级行政区完整名称 → 英文（中文全称） */
export const provinceMap: Record<string, string> = {
  '北京市': 'Beijing',
  '上海市': 'Shanghai',
  '天津市': 'Tianjin',
  '重庆市': 'Chongqing',
  '河北省': 'Hebei',
  '山西省': 'Shanxi',
  '辽宁省': 'Liaoning',
  '吉林省': 'Jilin',
  '黑龙江省': 'Heilongjiang',
  '江苏省': 'Jiangsu',
  '浙江省': 'Zhejiang',
  '安徽省': 'Anhui',
  '福建省': 'Fujian',
  '江西省': 'Jiangxi',
  '山东省': 'Shandong',
  '河南省': 'Henan',
  '湖北省': 'Hubei',
  '湖南省': 'Hunan',
  '广东省': 'Guangdong',
  '海南省': 'Hainan',
  '四川省': 'Sichuan',
  '贵州省': 'Guizhou',
  '云南省': 'Yunnan',
  '陕西省': 'Shaanxi',
  '甘肃省': 'Gansu',
  '青海省': 'Qinghai',
  '台湾省': 'Taiwan',
  '内蒙古自治区': 'Inner Mongolia',
  '广西壮族自治区': 'Guangxi',
  '西藏自治区': 'Tibet',
  '宁夏回族自治区': 'Ningxia',
  '新疆维吾尔自治区': 'Xinjiang',
  '香港特别行政区': 'Hong Kong',
  '澳门特别行政区': 'Macao'
};

/** 省级行政区简写映射（去掉"省/市/自治区"等后缀） */
export const provinceShortMap: Record<string, string> = {
  '北京': 'Beijing',
  '上海': 'Shanghai',
  '天津': 'Tianjin',
  '重庆': 'Chongqing',
  '河北': 'Hebei',
  '山西': 'Shanxi',
  '辽宁': 'Liaoning',
  '吉林': 'Jilin',
  '黑龙江': 'Heilongjiang',
  '江苏': 'Jiangsu',
  '浙江': 'Zhejiang',
  '安徽': 'Anhui',
  '福建': 'Fujian',
  '江西': 'Jiangxi',
  '山东': 'Shandong',
  '河南': 'Henan',
  '湖北': 'Hubei',
  '湖南': 'Hunan',
  '广东': 'Guangdong',
  '海南': 'Hainan',
  '四川': 'Sichuan',
  '贵州': 'Guizhou',
  '云南': 'Yunnan',
  '陕西': 'Shaanxi',
  '甘肃': 'Gansu',
  '青海': 'Qinghai',
  '台湾': 'Taiwan',
  '内蒙古': 'Inner Mongolia',
  '广西': 'Guangxi',
  '西藏': 'Tibet',
  '宁夏': 'Ningxia',
  '新疆': 'Xinjiang',
  '香港': 'Hong Kong',
  '澳门': 'Macao'
};

// ==================== 城市映射（主要城市） ====================

/** 城市完整名称 → 英文（中文全称带"市"后缀） */
export const cityMap: Record<string, string> = {
  // 直辖市
  '北京市': 'Beijing',
  '上海市': 'Shanghai',
  '天津市': 'Tianjin',
  '重庆市': 'Chongqing',

  // 河北
  '石家庄市': 'Shijiazhuang',
  '唐山市': 'Tangshan',
  '秦皇岛市': 'Qinhuangdao',
  '邯郸市': 'Handan',
  '邢台市': 'Xingtai',
  '保定市': 'Baoding',
  '张家口市': 'Zhangjiakou',
  '承德市': 'Chengde',
  '沧州市': 'Cangzhou',
  '廊坊市': 'Langfang',
  '衡水市': 'Hengshui',

  // 山西
  '太原市': 'Taiyuan',
  '大同市': 'Datong',
  '阳泉市': 'Yangquan',
  '长治市': 'Changzhi',
  '晋城市': 'Jincheng',
  '朔州市': 'Shuozhou',
  '晋中市': 'Jinzhong',
  '运城市': 'Yuncheng',
  '忻州市': 'Xinzhou',
  '临汾市': 'Linfen',
  '吕梁市': 'Lvliang',

  // 辽宁
  '沈阳市': 'Shenyang',
  '大连市': 'Dalian',
  '鞍山市': 'Anshan',
  '抚顺市': 'Fushun',
  '本溪市': 'Benxi',
  '丹东市': 'Dandong',
  '锦州市': 'Jinzhou',
  '营口市': 'Yingkou',
  '阜新市': 'Fuxin',
  '辽阳市': 'Liaoyang',
  '盘锦市': 'Panjin',
  '铁岭市': 'Tieling',
  '朝阳市': 'Chaoyang',
  '葫芦岛市': 'Huludao',

  // 吉林
  '长春市': 'Changchun',
  '吉林市': 'Jilin',
  '四平市': 'Siping',
  '辽源市': 'Liaoyuan',
  '通化市': 'Tonghua',
  '白山市': 'Baishan',
  '松原市': 'Songyuan',
  '白城市': 'Baicheng',
  '延吉市': 'Yanji',

  // 黑龙江
  '哈尔滨市': 'Harbin',
  '齐齐哈尔市': 'Qiqihar',
  '鸡西市': 'Jixi',
  '鹤岗市': 'Hegang',
  '双鸭山市': 'Shuangyashan',
  '大庆市': 'Daqing',
  '伊春市': 'Yichun',
  '佳木斯市': 'Jiamusi',
  '七台河市': 'Qitaihe',
  '牡丹江市': 'Mudanjiang',
  '黑河市': 'Heihe',
  '绥化市': 'Suihua',
  '大兴安岭市': 'Daxinganling',

  // 江苏
  '南京市': 'Nanjing',
  '无锡市': 'Wuxi',
  '徐州市': 'Xuzhou',
  '常州市': 'Changzhou',
  '苏州市': 'Suzhou',
  '南通市': 'Nantong',
  '连云港市': 'Lianyungang',
  '淮安市': 'Huaian',
  '盐城市': 'Yancheng',
  '扬州市': 'Yangzhou',
  '镇江市': 'Zhenjiang',
  '泰州市': 'Taizhou',
  '宿迁市': 'Suqian',

  // 浙江
  '杭州市': 'Hangzhou',
  '宁波市': 'Ningbo',
  '温州市': 'Wenzhou',
  '嘉兴市': 'Jiaxing',
  '湖州市': 'Huzhou',
  '绍兴市': 'Shaoxing',
  '金华市': 'Jinhua',
  '衢州市': 'Quzhou',
  '舟山市': 'Zhoushan',
  '台州市': 'Taizhou',
  '丽水市': 'Lishui',

  // 安徽
  '合肥市': 'Hefei',
  '芜湖市': 'Wuhu',
  '蚌埠市': 'Bengbu',
  '淮南市': 'Huainan',
  '马鞍山市': 'Maanshan',
  '淮北市': 'Huaibei',
  '铜陵市': 'Tongling',
  '安庆市': 'Anqing',
  '黄山市': 'Huangshan',
  '滁州市': 'Chuzhou',
  '阜阳市': 'Fuyang',
  '宿州市': 'Suzhou',
  '六安市': 'Lu\'an',
  '亳州市': 'Bozhou',
  '池州市': 'Chizhou',
  '宣城市': 'Xuancheng',

  // 福建
  '福州市': 'Fuzhou',
  '厦门市': 'Xiamen',
  '莆田市': 'Putian',
  '三明市': 'Sanming',
  '泉州市': 'Quanzhou',
  '漳州市': 'Zhangzhou',
  '南平市': 'Nanping',
  '龙岩市': 'Longyan',
  '宁德市': 'Ningde',

  // 江西
  '南昌市': 'Nanchang',
  '景德镇市': 'Jingdezhen',
  '萍乡市': 'Pingxiang',
  '九江市': 'Jiujiang',
  '新余市': 'Xinyu',
  '鹰潭市': 'Yingtan',
  '赣州市': 'Ganzhou',
  '吉安市': 'Jian',
  '宜春市': 'Yichun',
  '抚州市': 'Fuzhou',
  '上饶市': 'Shangrao',

  // 山东
  '济南市': 'Jinan',
  '青岛市': 'Qingdao',
  '淄博市': 'Zibo',
  '枣庄市': 'Zaozhuang',
  '东营市': 'Dongying',
  '烟台市': 'Yantai',
  '潍坊市': 'Weifang',
  '济宁市': 'Jining',
  '泰安市': 'Tai\'an',
  '威海市': 'Weihai',
  '日照市': 'Rizhao',
  '临沂市': 'Linyi',
  '德州市': 'Dezhou',
  '聊城市': 'Liaocheng',
  '滨州市': 'Binzhou',
  '菏泽市': 'Heze',

  // 河南
  '郑州市': 'Zhengzhou',
  '开封市': 'Kaifeng',
  '洛阳市': 'Luoyang',
  '平顶山市': 'Pingdingshan',
  '安阳市': 'Anyang',
  '鹤壁市': 'Hebi',
  '新乡市': 'Xinxiang',
  '焦作市': 'Jiaozuo',
  '濮阳市': 'Puyang',
  '许昌市': 'Xuchang',
  '漯河市': 'Luohe',
  '三门峡市': 'Sanmenxia',
  '南阳市': 'Nanyang',
  '商丘市': 'Shangqiu',
  '信阳市': 'Xinyang',
  '周口市': 'Zhoukou',
  '驻马店市': 'Zhumadian',

  // 湖北
  '武汉市': 'Wuhan',
  '黄石市': 'Huangshi',
  '十堰市': 'Shiyan',
  '宜昌市': 'Yichang',
  '襄阳市': 'Xiangyang',
  '鄂州市': 'Ezhou',
  '荆门市': 'Jingmen',
  '孝感市': 'Xiaogan',
  '荆州市': 'Jingzhou',
  '黄冈市': 'Huanggang',
  '咸宁市': 'Xianning',
  '随州市': 'Suizhou',
  '恩施市': 'Enshi',

  // 湖南
  '长沙市': 'Changsha',
  '株洲市': 'Zhuzhou',
  '湘潭市': 'Xiangtan',
  '衡阳市': 'Hengyang',
  '邵阳市': 'Shaoyang',
  '岳阳市': 'Yueyang',
  '常德市': 'Changde',
  '张家界市': 'Zhangjiajie',
  '益阳市': 'Yiyang',
  '郴州市': 'Chenzhou',
  '永州市': 'Yongzhou',
  '怀化市': 'Huaihua',
  '娄底市': 'Loudi',

  // 广东
  '广州市': 'Guangzhou',
  '深圳市': 'Shenzhen',
  '珠海市': 'Zhuhai',
  '汕头市': 'Shantou',
  '佛山市': 'Foshan',
  '韶关市': 'Shaoguan',
  '湛江市': 'Zhanjiang',
  '肇庆市': 'Zhaoqing',
  '江门市': 'Jiangmen',
  '茂名市': 'Maoming',
  '惠州市': 'Huizhou',
  '梅州市': 'Meizhou',
  '汕尾市': 'Shanwei',
  '河源市': 'Heyuan',
  '阳江市': 'Yangjiang',
  '清远市': 'Qingyuan',
  '东莞市': 'Dongguan',
  '中山市': 'Zhongshan',
  '潮州市': 'Chaozhou',
  '揭阳市': 'Jieyang',
  '云浮市': 'Yunfu',

  // 海南
  '海口市': 'Haikou',
  '三亚市': 'Sanya',
  '三沙市': 'Sansha',
  '儋州市': 'Danzhou',

  // 四川
  '成都市': 'Chengdu',
  '自贡市': 'Zigong',
  '攀枝花市': 'Panzhihua',
  '泸州市': 'Luzhou',
  '德阳市': 'Deyang',
  '绵阳市': 'Mianyang',
  '广元市': 'Guangyuan',
  '遂宁市': 'Suining',
  '内江市': 'Neijiang',
  '乐山市': 'Leshan',
  '南充市': 'Nanchong',
  '眉山市': 'Meishan',
  '宜宾市': 'Yibin',
  '广安市': 'Guangan',
  '达州市': 'Dazhou',
  '雅安市': 'Yaan',
  '巴中市': 'Bazhong',
  '资阳市': 'Ziyang',

  // 贵州
  '贵阳市': 'Guiyang',
  '六盘水市': 'Liupanshui',
  '遵义市': 'Zunyi',
  '安顺市': 'Anshun',
  '毕节市': 'Bijie',
  '铜仁市': 'Tongren',

  // 云南
  '昆明市': 'Kunming',
  '曲靖市': 'Qujing',
  '玉溪市': 'Yuxi',
  '保山市': 'Baoshan',
  '昭通市': 'Zhaotong',
  '丽江市': 'Lijiang',
  '普洱市': 'Puer',
  '临沧市': 'Lincang',
  '大理市': 'Dali',
  '楚雄市': 'Chuxiong',
  '红河市': 'Honghe',
  '文山市': 'Wenshan',
  '西双版纳市': 'Xishuangbanna',

  // 陕西
  '西安市': 'Xi\'an',
  '铜川市': 'Tongchuan',
  '宝鸡市': 'Baoji',
  '咸阳市': 'Xianyang',
  '渭南市': 'Weinan',
  '延安市': 'Yan\'an',
  '汉中市': 'Hanzhong',
  '榆林市': 'Yulin',
  '安康市': 'Ankang',
  '商洛市': 'Shangluo',

  // 甘肃
  '兰州市': 'Lanzhou',
  '嘉峪关市': 'Jiayuguan',
  '金昌市': 'Jinchang',
  '白银市': 'Baiyin',
  '天水市': 'Tianshui',
  '武威市': 'Wuwei',
  '张掖市': 'Zhangye',
  '平凉市': 'Pingliang',
  '酒泉市': 'Jiuquan',
  '庆阳市': 'Qingyang',
  '定西市': 'Dingxi',
  '陇南市': 'Longnan',

  // 青海
  '西宁市': 'Xining',
  '海东市': 'Haidong',

  // 台湾
  '台北市': 'Taipei',
  '高雄市': 'Kaohsiung',
  '基隆市': 'Keelung',
  '台中市': 'Taichung',
  '台南市': 'Tainan',
  '新竹市': 'Hsinchu',
  '嘉义市': 'Chiayi',

  // 内蒙古
  '呼和浩特市': 'Hohhot',
  '包头市': 'Baotou',
  '乌海市': 'Wuhai',
  '赤峰市': 'Chifeng',
  '通辽市': 'Tongliao',
  '鄂尔多斯市': 'Ordos',
  '呼伦贝尔市': 'Hulunbuir',
  '巴彦淖尔市': 'Bayannur',
  '乌兰察布市': 'Ulanqab',

  // 广西
  '南宁市': 'Nanning',
  '柳州市': 'Liuzhou',
  '桂林市': 'Guilin',
  '梧州市': 'Wuzhou',
  '北海市': 'Beihai',
  '防城港市': 'Fangchenggang',
  '钦州市': 'Qinzhou',
  '贵港市': 'Guigang',
  '玉林市': 'Yulin',
  '百色市': 'Baise',
  '贺州市': 'Hezhou',
  '河池市': 'Hechi',
  '来宾市': 'Laibin',
  '崇左市': 'Chongzuo',

  // 西藏
  '拉萨市': 'Lhasa',
  '日喀则市': 'Shigatse',
  '昌都市': 'Qamdo',
  '林芝市': 'Nyingchi',
  '山南市': 'Shannan',

  // 宁夏
  '银川市': 'Yinchuan',
  '石嘴山市': 'Shizuishan',
  '吴忠市': 'Wuzhong',
  '固原市': 'Guyuan',
  '中卫市': 'Zhongwei',

  // 新疆
  '乌鲁木齐市': 'Urumqi',
  '克拉玛依市': 'Karamay',
  '吐鲁番市': 'Turpan',
  '哈密市': 'Hami',
  '喀什市': 'Kashgar',
  '和田市': 'Hotan',
  '阿克苏市': 'Aksu',
  '库尔勒市': 'Korla',
  '伊宁市': 'Yining',

  // 香港
  '香港岛': 'Hong Kong Island',
  '九龙': 'Kowloon',
  '新界': 'New Territories',

  // 澳门
  '澳门半岛': 'Macao Peninsula',
  '氹仔': 'Taipa',
  '路环': 'Coloane'
};

// ==================== 城市简写映射（去掉"市"后缀） ====================

export const cityShortMap: Record<string, string> = {};
{
  const keys = Object.keys(cityMap);
  for (const fullName of keys) {
    const shortName = fullName.replace('市', '');
    cityShortMap[shortName] = cityMap[fullName] as string;
  }
}

// ==================== 已知冲突列表 ====================

const KNOWN_CONFLICTS: Record<string, ConflictEntry> = {
  'jilin':  { default: '吉林省',     alternatives: ['吉林市'] },
  'yichun': { default: '宜春市',     alternatives: ['伊春市'] },
  'suzhou': { default: '苏州市',     alternatives: ['宿州市'] },
  'taizhou':{ default: '台州市',     alternatives: ['泰州市'] },
  'fuzhou': { default: '福州市',     alternatives: ['抚州市'] },
  'yulin':  { default: '玉林市',     alternatives: ['榆林市'] }
};

// ==================== 自动生成反向映射 ====================

function _reverseMap(map: Record<string, string>): ReverseMap {
  const reversed: ReverseMap = {};
  const entries = Object.entries(map);

  for (const [cn, en] of entries) {
    const lower = en.toLowerCase();
    const conflict = KNOWN_CONFLICTS[lower];

    if (reversed[en] && conflict) {
      // 保留 default 指向的那一项
      if (cn === conflict.default) {
        reversed[en] = cn;
        reversed[lower] = cn;
      }
      // 其他项跳过，不覆盖
      continue;
    }

    reversed[en] = cn;
    reversed[lower] = cn;
  }

  return reversed;
}

export const provinceMapEnToCn: ReverseMap  = _reverseMap(provinceMap);
export const provinceShortMapEnToCn: ReverseMap = _reverseMap(provinceShortMap);
export const cityMapEnToCn: ReverseMap      = _reverseMap(cityMap);
export const cityShortMapEnToCn: ReverseMap  = _reverseMap(cityShortMap);

// ==================== 转换函数 ====================

/**
 * 中文 → 英文
 * @param location - 中文地名
 * @param type     - 转换范围：'auto' | 'province' | 'city'
 * @returns 英文地名（未匹配时返回原值）
 */
export function cnToEn(location: string, type: TranslateType = 'auto'): string {
  if (!location || typeof location !== 'string') return '';

  // 1. 完整匹配
  if ((type === 'province' || type === 'auto') && provinceMap[location]) {
    return provinceMap[location] as string;
  }
  if ((type === 'city' || type === 'auto') && cityMap[location]) {
    return cityMap[location] as string;
  }

  // 2. 去掉行政后缀再试
  const noSuffix = location.replace(/(省|市|自治区|特别行政区|地区|州|盟)$/g, '');

  if ((type === 'province' || type === 'auto') && provinceShortMap[noSuffix]) {
    return provinceShortMap[noSuffix] as string;
  }
  if ((type === 'city' || type === 'auto') && cityShortMap[noSuffix]) {
    return cityShortMap[noSuffix] as string;
  }

  return location;
}

/**
 * 英文 → 中文
 * @param location - 英文地名
 * @param type     - 转换范围：'auto' | 'province' | 'city'
 * @returns 中文地名（未匹配时返回原值）
 */
export function enToCn(location: string, type: TranslateType = 'auto'): string {
  if (!location || typeof location !== 'string') return '';

  const original: string = location;
  const lower: string = location.toLowerCase();

  // 1. 精确匹配（大小写敏感 + 小写）
  if (type === 'province' || type === 'auto') {
    if (provinceMapEnToCn[original]) return provinceMapEnToCn[original] as string;
    if (provinceMapEnToCn[lower])    return provinceMapEnToCn[lower] as string;
  }
  if (type === 'city' || type === 'auto') {
    if (cityMapEnToCn[original]) return cityMapEnToCn[original] as string;
    if (cityMapEnToCn[lower])    return cityMapEnToCn[lower] as string;
  }

  // 2. 清理常见英文后缀后再试
  const cleanEn: string = original
    .replace(/(city|municipality|province|region|state|county|prefecture)$/i, '')
    .trim();
  const cleanLower: string = cleanEn.toLowerCase();

  if (type === 'province' || type === 'auto') {
    if (provinceMapEnToCn[cleanEn])    return provinceMapEnToCn[cleanEn] as string;
    if (provinceMapEnToCn[cleanLower]) return provinceMapEnToCn[cleanLower] as string;
  }
  if (type === 'city' || type === 'auto') {
    if (cityMapEnToCn[cleanEn])    return cityMapEnToCn[cleanEn] as string;
    if (cityMapEnToCn[cleanLower]) return cityMapEnToCn[cleanLower] as string;
  }

  // 3. 带显式后缀的冲突处理
  if (/(province|region|state)$/i.test(original)) {
    if (provinceMapEnToCn[cleanEn])    return provinceMapEnToCn[cleanEn] as string;
    const provFromCity = cityMapEnToCn[cleanEn];
    if (provFromCity && provFromCity.endsWith('省')) return provFromCity;
  }
  if (/(city|municipality)$/i.test(original)) {
    if (cityMapEnToCn[cleanEn]) return cityMapEnToCn[cleanEn] as string;
  }

  return original;
}

/**
 * 智能转换：自动检测输入语言
 * @param location - 地名（中文或英文）
 * @returns 转换后的地名
 */
export function translate(location: string): string {
  if (!location || typeof location !== 'string') return '';

  const hasChinese: boolean = /[\u4e00-\u9fa5]/.test(location);
  return hasChinese ? cnToEn(location) : enToCn(location);
}

/**
 * 批量转换
 * @param locations - 地名数组
 * @param options   - 选项（可指定方向）
 * @returns 转换后的数组
 */
export function batchTranslate(
  locations: string[],
  options: BatchTranslateOptions = {}
): string[] {
  const { direction = 'auto', type = 'auto' } = options;

  return locations.map((loc: string): string => {
    if (direction === 'cnToEn') return cnToEn(loc, type);
    if (direction === 'enToCn') return enToCn(loc, type);
    return translate(loc);
  });
}

/**
 * 完整地址中英文互转
 * 例：'中国广东省深圳市' → 'China Guangdong Shenzhen'
 *     'Shenzhen, Guangdong, China' → '深圳市广东省中国'
 */
export function translateAddress(
  address: string,
  direction: BatchDirection = 'auto'
): string {
  if (!address) return '';

  const hasChinese: boolean = /[\u4e00-\u9fa5]/.test(address);
  const isCnToEn: boolean =
    direction === 'cnToEn' || (direction === 'auto' && hasChinese);

  // 常见国家名映射
  const countryMap: Record<string, string> = {
    '中国': 'China',  'CN': 'China',  'China': '中国',
    '美国': 'United States', 'USA': 'United States', 'US': 'United States',
    '日本': 'Japan',  'JP': 'Japan',  'Japan': '日本',
    '韩国': 'South Korea', 'KR': 'South Korea', 'South Korea': '韩国',
    '英国': 'United Kingdom', 'UK': 'United Kingdom', 'United Kingdom': '英国',
    '法国': 'France', 'FR': 'France', 'France': '法国',
    '德国': 'Germany', 'DE': 'Germany', 'Germany': '德国',
    '澳大利亚': 'Australia', 'AU': 'Australia', 'Australia': '澳大利亚',
    '加拿大': 'Canada', 'CA': 'Canada', 'Canada': '加拿大',
    '新加坡': 'Singapore', 'SG': 'Singapore', 'Singapore': '新加坡',
    '马来西亚': 'Malaysia', 'MY': 'Malaysia', 'Malaysia': '马来西亚',
    '泰国': 'Thailand', 'TH': 'Thailand', 'Thailand': '泰国',
    '越南': 'Vietnam', 'VN': 'Vietnam', 'Vietnam': '越南',
    '印度': 'India', 'IN': 'India', 'India': '印度',
    '俄罗斯': 'Russia', 'RU': 'Russia', 'Russia': '俄罗斯',
    '巴西': 'Brazil', 'BR': 'Brazil', 'Brazil': '巴西'
  };

  if (isCnToEn) {
    let result: string = address;

    // 替换国家
    const countryEntries = Object.entries(countryMap);
    for (const [cn, en] of countryEntries) {
      if (/^[A-Z]{2}$/.test(cn)) continue;
      result = result.replace(new RegExp(cn, 'g'), ' ' + en + ' ');
    }

    // 替换省级（长的先替换，避免部分匹配）
    const sortedProvinces = Object.entries(provinceMap).sort(
      (a, b) => b[0].length - a[0].length
    );
    for (const [cn, en] of sortedProvinces) {
      result = result.replace(new RegExp(cn, 'g'), ' ' + en + ' ');
    }

    // 替换城市（长的先替换）
    const sortedCities = Object.entries(cityMap).sort(
      (a, b) => b[0].length - a[0].length
    );
    for (const [cn, en] of sortedCities) {
      result = result.replace(new RegExp(cn, 'g'), ' ' + en + ' ');
    }

    return result.replace(/\s+/g, ' ').trim();
  }

  // ---- 英文 → 中文 ----
  let result: string = address;

  // 替换国家
  const countryEntries = Object.entries(countryMap);
  for (const [cn, en] of countryEntries) {
    if (/^[A-Z]{2}$/.test(en)) continue;
    result = result.replace(new RegExp('\\b' + en + '\\b', 'gi'), cn);
  }

  // 替换省级
  const sortedProv = Object.entries(provinceMap).sort(
    (a, b) => b[1].length - a[1].length
  );
  for (const [cn, en] of sortedProv) {
    const escaped = en.replace(/'/g, "\\'");
    result = result.replace(new RegExp('\\b' + escaped + '\\b', 'gi'), cn);
  }

  // 替换城市
  const sortedCity = Object.entries(cityMap).sort(
    (a, b) => b[1].length - a[1].length
  );
  for (const [cn, en] of sortedCity) {
    const escaped = en.replace(/'/g, "\\'");
    result = result.replace(new RegExp('\\b' + escaped + '\\b', 'gi'), cn);
  }

  return result;
}

// ==================== 默认导出（方便 import 解构） ====================

export default {
  provinceMap,
  provinceShortMap,
  cityMap,
  cityShortMap,
  provinceMapEnToCn,
  cityMapEnToCn,
  cnToEn,
  enToCn,
  translate,
  batchTranslate,
  translateAddress
};
